import { FC } from 'react';
import {
  Button,
  Modal,
  Spacer,
  Stack,
  TextBody,
  TextTitle,
} from '@pypestream/design-system';

export type ConfirmMetabaseUpgradeVariant = 'upgrade' | 'fullAccess';

export type ConfirmMetabaseUpgradeModalProps = {
  open: boolean;
  variant: ConfirmMetabaseUpgradeVariant;
  projectName: string;
  targetRoleName: string;
  currentRoleName?: string;
  isSaving?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

export const ConfirmMetabaseUpgradeModal: FC<
  ConfirmMetabaseUpgradeModalProps
> = ({
  open,
  variant,
  projectName,
  targetRoleName,
  currentRoleName,
  isSaving = false,
  onCancel,
  onConfirm,
}) => {
  const isFullAccess = variant === 'fullAccess';

  const title = isFullAccess
    ? 'Confirm Full Access'
    : 'Confirm Permission Upgrade';
  const confirmLabel = isFullAccess
    ? 'Grant Full Access'
    : `Upgrade to ${targetRoleName}`;

  const body = isFullAccess ? (
    currentRoleName ? (
      <TextBody variant="secondary">
        This user has the <strong>{currentRoleName}</strong> role for specific
        dashboard groups in the <strong>{projectName}</strong> Project.
        Upgrading them to <strong>{targetRoleName}</strong> also grants full
        access to every dashboard group in the <strong>{projectName}</strong>{' '}
        Project, including the ones added later.
      </TextBody>
    ) : (
      <TextBody variant="secondary">
        This user has access to specific dashboard groups in the{' '}
        <strong>{projectName}</strong> Project. Granting them{' '}
        <strong>{targetRoleName}</strong> gives full access to every dashboard
        group in the <strong>{projectName}</strong> Project, including the ones
        added later.
      </TextBody>
    )
  ) : currentRoleName ? (
    <TextBody variant="secondary">
      This user has the <strong>{currentRoleName}</strong> role in the{' '}
      <strong>{projectName}</strong> Project. Upgrade them to the{' '}
      <strong>{targetRoleName}</strong> role?
    </TextBody>
  ) : (
    <TextBody variant="secondary">
      Grant this user the <strong>{targetRoleName}</strong> role in the{' '}
      <strong>{projectName}</strong> Project? This gives full access to every
      dashboard group, including the ones added later.
    </TextBody>
  );

  return (
    <Modal open={open} size="medium" onClose={onCancel}>
      <TextTitle slot="title" size="small">
        {title}
      </TextTitle>

      <Stack slot="header">
        <Spacer size="medium" />
        {body}
      </Stack>
      <Stack slot="footer" justifyContent="end" gutter="small">
        <Button
          variant="secondary"
          size="large"
          onClick={onCancel}
          disabled={isSaving}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          size="large"
          loading={isSaving}
          onClick={onConfirm}
        >
          {confirmLabel}
        </Button>
      </Stack>
    </Modal>
  );
};
